import { Question } from '@verzekeringapp-mono/shared/src/models/value_meter_question'
import { Controller, useFormContext } from 'react-hook-form'
import { QuestionLabel } from './QuestionLabel'
import { RadioInput } from './RadioInput'
import TextInput from '~/components/TextInput'
import { useCallback } from 'react'
import CalculationField from './CalculationField'
import { laravelValidate } from '@verzekeringapp-mono/shared/src/utils/laravel_validation'
import { getNormalizedRulesForQuestion } from '@verzekeringapp-mono/shared/src/utils/laravel_normalize_rules'
import useCalculation from '@verzekeringapp-mono/shared/src/hooks/useCalculation'

export const ValueMeterFormQuestions = ({
  questions,
  parentName,
  watch,
  setValue,
  control,
  disabled
}: {
  questions: Record<string, Question> | undefined
  parentName?: string,
  setValue: ReturnType<typeof useFormContext>['setValue']
  watch: ReturnType<typeof useFormContext>['watch']
  control: ReturnType<typeof useFormContext>['control']
  disabled?: boolean
}) => {
  const calculation = useCalculation(watch)

  const canRenderQuestion = useCallback(
    (question: Question, parentValue: string) => (
      question.condition === parentValue ||
      (Array.isArray(question.condition) && question.condition.includes(parentValue))
    ), [])

  if (!questions) return null

  return Object.values(questions).map(question => {
    if (parentName) {
      const parentValue = watch(parentName)

      if (!canRenderQuestion(question, parentValue)) {
        return null
      }
    }

    return (
      <div
        key={question.name}
        className={`${!parentName ? 'mb-4' : ''} flex flex-col gap-2`}
      >
        {question.label && (
          <QuestionLabel
            label={question.label}
            info={question.info}
          />)}

        {question.type === 'radio' && (
          <RadioInput
            name={question.name}
            options={question.options}
            hint={question.hint}
            rules={question.rules}
            disabled={disabled || question.isReadOnly === 'true'}
          />
        )}

        {question.type === 'number' && (
          <Controller
            name={question.name}
            control={control}
            defaultValue=''
            rules={{
              validate: laravelValidate(getNormalizedRulesForQuestion(question))
            }}
            render={({ field, fieldState }) => (
              <TextInput
                type={question.type}
                showValidationIcon={false}
                shouldValidate={true}
                showValidationText={true}
                validationText={fieldState.error ? fieldState.error.message || 'Ongeldige invoer' : ''}
                placeholder={question.label}
                icon={'euro'}
                className='mt-0 mb-0'
                {...field}
                isValid={!fieldState.error}
                disabled={disabled || question.isReadOnly === 'true'}
              />
            )}
          />
        )}

        {question.type === 'calculation' && (
          <CalculationField
            question={question}
            control={control}
            setValue={setValue}
            calculation={calculation}
          />
        )}

        <ValueMeterFormQuestions
          questions={question.subQuestions}
          parentName={question.name}
          setValue={setValue}
          watch={watch}
          control={control}
          disabled={disabled || question.isReadOnly === 'true'}
        />
      </div>
    )
  })
}
